<template>
  <div class="graduates-table-container">
    <!-- 表格区域 -->
    <div class="table-area">
      <div class="table-responsive" ref="tableRef">
        <table class="stable-table">
          <thead>
          <tr>
            <th>姓名</th>
            <th>期别</th>
            <th>专业</th>
            <th>原籍</th>
            <th>详情</th>
          </tr>
          </thead>
          <tbody>
          <template v-if="loading">
            <tr v-for="n in pagination.pageSize" :key="`skeleton-${n}`">
              <td><div class="skeleton-box"></div></td>
              <td><div class="skeleton-box"></div></td>
              <td><div class="skeleton-box"></div></td>
              <td><div class="skeleton-box"></div></td>
              <td><div class="skeleton-box"></div></td>
            </tr>
          </template>
          <template v-else>
            <tr v-if="!graduates || graduates.length === 0">
              <td colspan="5" class="no-data">没有找到匹配的记录</td>
            </tr>
            <tr v-for="graduate in graduates" :key="graduate.id">
              <td>{{ graduate.name }}</td>
              <td>{{ graduate.period }}</td>
              <td>{{ graduate.major }}</td>
              <td>{{ graduate.origin }}</td>
              <td>
                <button class="detail-btn" @click="emit('show-detail', graduate.id)">
                  查看详情
                </button>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 增强后的分页控件 -->
    <div class="pagination-container" v-if="pagination.totalItems > 0">
      <div class="pagination-info">
        显示 {{ pagination.startItem }}-{{ pagination.endItem }} 条，共 {{ pagination.totalItems }} 条
      </div>
      <div class="pagination-controls">
        <div class="page-size-selector">
          <span>每页</span>
          <select v-model="pagination.pageSize" @change="handlePageSizeChange">
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="30">30</option>
            <option value="50">50</option>
          </select>
          <span>条</span>
        </div>
        <div class="page-numbers">
          <!-- 首页按钮 -->
          <button
              class="page-btn first"
              :disabled="pagination.currentPage === 1"
              @click="goToPage(1)"
              title="首页"
          >
            &laquo;
          </button>

          <button
              class="page-btn prev"
              :disabled="pagination.currentPage === 1"
              @click="goToPage(pagination.currentPage - 1)"
              title="上一页"
          >
            &lsaquo;
          </button>

          <!-- 页码输入框 -->
          <div class="page-input">
            <input
                type="number"
                min="1"
                :max="pagination.totalPages"
                :value="pagination.currentPage"
                @change="handlePageInput"
                @keyup.enter="handlePageInput"
            />
            <span>/ {{ pagination.totalPages }}</span>
          </div>

          <button
              class="page-btn next"
              :disabled="pagination.currentPage === pagination.totalPages"
              @click="goToPage(pagination.currentPage + 1)"
              title="下一页"
          >
            &rsaquo;
          </button>

          <!-- 末页按钮 -->
          <button
              class="page-btn last"
              :disabled="pagination.currentPage === pagination.totalPages"
              @click="goToPage(pagination.totalPages)"
              title="末页"
          >
            &raquo;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import api from "@/services/api";

const props = defineProps({
  searchParams: {
    type: Object,
    required: true
  },
  periods: {
    type: Array,
    default: null
  },
  majors: {
    type: Array,
    default: null
  }
})

const emit = defineEmits(['show-detail'])

// 分页数据
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: computed(() => Math.ceil(pagination.totalItems / pagination.pageSize)),
  startItem: computed(() => (pagination.currentPage - 1) * pagination.pageSize + 1),
  endItem: computed(() => Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)),
  maxVisiblePages: 5,
  startPage: computed(() => {
    if (pagination.totalPages <= pagination.maxVisiblePages) return 1
    return Math.max(1, Math.min(
        pagination.currentPage - Math.floor(pagination.maxVisiblePages / 2),
        pagination.totalPages - pagination.maxVisiblePages + 1
    ))
  }),
  endPage: computed(() => {
    return Math.min(pagination.startPage + pagination.maxVisiblePages - 1, pagination.totalPages)
  }),
  pages: computed(() => {
    const pages = []
    const start = pagination.startPage
    const end = pagination.endPage
    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    return pages
  })
})

// 毕业生数据
const graduates = ref(null)
const loading = ref(false)
const tableRef = ref(null)

// 获取毕业生数据
const fetchGraduates = async () => {
  try {
    loading.value = true
    const params = {
      ...props.searchParams,
      pageNum: pagination.currentPage,
      pageSize: pagination.pageSize
    }

    const response = await api.getGraduates(params)
    if (response.data.rows) {
      graduates.value = response.data.rows
      pagination.totalItems = response.data.total
    }
  } catch (error) {
    console.error('获取毕业生数据失败:', error)
    graduates.value = []
    pagination.totalItems = 0
  } finally {
    loading.value = false
  }
}

// 改变每页显示条数
const handlePageSizeChange = () => {
  pagination.currentPage = 1
  fetchGraduates()
}

// 跳转到指定页
const goToPage = (page) => {
  if (page >= 1 && page <= pagination.totalPages && page !== pagination.currentPage) {
    pagination.currentPage = page
    fetchGraduates()
  }
}

// 监听搜索参数变化
watch(() => props.searchParams, () => {
  pagination.currentPage = 1
  fetchGraduates()
}, { deep: true })


// 初始化加载数据
fetchGraduates()

// 添加页码输入处理
const handlePageInput = (event) => {
  let page = parseInt(event.target.value)
  if (isNaN(page) ){
    return
  }

  // 确保页码在有效范围内
  page = Math.max(1, Math.min(page, pagination.totalPages))
  goToPage(page)
}
</script>

<style scoped>
.graduates-table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px; /* 保证最小高度 */
}

.table-area {
  flex: 1;
  min-height: 300px;
  overflow: hidden;
  position: relative;
}

.table-responsive {
  height: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.stable-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed; /* 固定表格布局防止跳动 */
}

.stable-table th,
.stable-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid var(--medium-gray);
  vertical-align: middle;
}

.stable-table th {
  position: sticky;
  top: 0;
  background-color: var(--primary-dark);
  color: white;
  font-weight: 500;
  z-index: 10;
}

.skeleton-box {
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 4px;
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.no-data {
  padding: 20px;
  text-align: center;
  color: var(--secondary-color);
}

/* 分页样式重构 */
.pagination-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid var(--medium-gray);
}

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap; /* 禁止换行 */
  overflow-x: auto; /* 允许横向滚动 */
  gap: 10px;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.page-size-selector select {
  padding: 6px 10px;
  border: 1px solid var(--medium-gray);
  border-radius: 4px;
}

.page-numbers {
  display: flex;
  gap: 5px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 2px; /* 为滚动条留空间 */
}

.page-btn {
  min-width: 32px;
  padding: 6px 10px;
  border: 1px solid var(--medium-gray);
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.page-btn:hover:not(:disabled) {
  background: var(--light-gray);
}

.page-btn.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .pagination-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .page-numbers {
    justify-content: center;
  }
}
/* 恢复原详情按钮样式 */
.detail-btn {
  padding: clamp(6px, 1vw, 8px) clamp(12px, 1.5vw, 15px);
  background-color: var(--primary-dark);
  color: var(--white);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: var(--transition);
  font-weight: 500;
  font-size: clamp(0.8rem, 1.6vw, 0.9rem);
}

.detail-btn:hover {
  background-color: var(--primary-dark);
}

/* 添加总页数样式 */
.total-pages {
  padding: 0 10px;
  font-size: clamp(0.8rem, 1.6vw, 0.9rem);
  color: var(--secondary-color);
  display: flex;
  align-items: center;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .total-pages {
    display: none; /* 小屏幕隐藏总页数以节省空间 */
  }
}

/* 页码输入框样式 */
.page-input {
  display: flex;
  align-items: center;
  margin: 0 5px;
}

.page-input input {
  width: 50px;
  padding: 6px;
  text-align: center;
  border: 1px solid var(--medium-gray);
  border-radius: 4px;
  margin-right: 5px;
}

.page-input span {
  font-size: 0.9em;
  color: var(--secondary-color);
}

/* 首页/末页按钮样式 */
.page-btn.first,
.page-btn.last {
  padding: 6px 8px;
  font-size: 1.1em;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .page-input {
    margin: 0 3px;
  }

  .page-input input {
    width: 40px;
    padding: 4px;
  }

  .page-btn.first,
  .page-btn.last {
    display: none; /* 小屏幕隐藏首页/末页按钮 */
  }
}
</style>